/**
 * API utility functions for handling HTTP responses
 * Shared across the application
 */

import { appConfig } from "@/config/app.config"

/**
 * Safely parses JSON from a Response object
 */
export const parseJsonSafe = async <T>(response: Response): Promise<T | null> => {
  try {
    const textBody = await response.clone().text()
    return textBody ? (JSON.parse(textBody) as T) : null
  } catch {
    return null
  }
}

/**
 * Lấy URL ảnh đầy đủ từ path
 */
export function getImageUrl(imagePath: string | null | undefined, fallback: string = "/assets/images/placeholder.jpg"): string {
  if (!imagePath) return fallback
  const normalizedPath = imagePath.trim()
  if (!normalizedPath) return fallback
  
  const isServer = typeof window === "undefined"
  const apiUrl = isServer ? appConfig.api.serverUrl : appConfig.api.publicUrl

  const getSiteOrigin = (): string => {
    const raw = appConfig.app.siteUrl || "https://tuyensinhnew.hub.edu.vn"
    try {
      return new URL(raw).origin
    } catch {
      const normalized = raw.startsWith("http://") || raw.startsWith("https://") ? raw : `https://${raw}`
      try {
        return new URL(normalized).origin
      } catch {
        return "https://tuyensinhnew.hub.edu.vn"
      }
    }
  }

  const withSiteOrigin = (pathWithQuery: string): string => {
    if (!pathWithQuery.startsWith("/")) return pathWithQuery
    return `${getSiteOrigin()}${pathWithQuery}`
  }
  
  // 1. URL tuyệt đối (http:// hoặc https://)
  if (normalizedPath.startsWith("http://") || normalizedPath.startsWith("https://")) {
    try {
      let url = new URL(normalizedPath)

      // Unwrap nested Next.js optimization URL: /_next/image?url=<inner>&w=...&q=...
      if (url.pathname.includes("/_next/image")) {
        const inner = url.searchParams.get("url")
        if (inner) {
          // decode and build new URL from inner
          url = new URL(inner, url)
        }
      }

      const siteHostname = new URL(appConfig.app.siteUrl).hostname
      const allowedHostnames = [
        "tuyensinhnew.hub.edu.vn",
        "tuyensinh.hub.edu.vn",
        "fileserver2.hub.edu.vn",
        "hub.edu.vn",
        siteHostname,
      ]

      // CMS cũ: file trên đĩa tại uploads/images/admincp/<folder>/
      // (vd: buh_tintuc_thumb, buh_slidehome, buh_quangcao)
      const legacyAdmincp = url.pathname.match(
        /\/admincp\/(buh_tintuc_thumb|buh_slidehome|buh_quangcao)\/([^/?#]+)$/i,
      )
      if (legacyAdmincp?.[1] && legacyAdmincp[2] && allowedHostnames.includes(url.hostname)) {
        const folder = legacyAdmincp[1]
        const file = decodeURIComponent(legacyAdmincp[2])
        return withSiteOrigin(
          `/api/admin/uploads/serve/images/admincp/${folder}/${file}`,
        )
      }

      // Nếu là domain của site hiện tại HOẶC domain chính của hệ thống
      // Chúng ta ép buộc trả về relative path để Next.js fetch qua localhost/rewrites
      if (allowedHostnames.includes(url.hostname)) {
        if (url.pathname.startsWith("/api/") || 
            url.pathname.startsWith("/assets/") || 
            url.pathname.startsWith("/images/")) {
          if (url.pathname.startsWith("/api/")) {
            return url.toString()
          }
          return url.pathname + url.search
        }
      }
      
      // Nếu không thuộc allowedHostnames, log cảnh báo (không chặn hiển thị)
      if (!allowedHostnames.includes(url.hostname)) {
        // console.warn(`[getImageUrl] Domain ${url.hostname} không thuộc danh sách cho phép`)
      }
      
      return url.toString()
    } catch {
      return normalizedPath
    }
  }

  // 1b. Legacy (không có domain): uploads/images/admincp/<folder>/ trên đĩa
  const relEarly = normalizedPath.replace(/^\/+/, "")
  const diskLegacyEarly = relEarly.match(
    /^images\/admincp\/(buh_tintuc_thumb|buh_slidehome|buh_quangcao)\/([^/?#]+)$/i,
  )
  if (diskLegacyEarly?.[1] && diskLegacyEarly[2]) {
    return withSiteOrigin(
      `/api/admin/uploads/serve/images/admincp/${diskLegacyEarly[1]}/${decodeURIComponent(diskLegacyEarly[2])}`,
    )
  }
  const admincpOnlyEarly = relEarly.match(
    /^admincp\/(buh_tintuc_thumb|buh_slidehome|buh_quangcao)\/([^/?#]+)$/i,
  )
  if (admincpOnlyEarly?.[1] && admincpOnlyEarly[2]) {
    return withSiteOrigin(
      `/api/admin/uploads/serve/images/admincp/${admincpOnlyEarly[1]}/${decodeURIComponent(admincpOnlyEarly[2])}`,
    )
  }

  // 2. Path nội bộ frontend (/assets/ hoặc /images/)
  if (normalizedPath.startsWith("/assets/") || normalizedPath.startsWith("assets/") || 
      normalizedPath.startsWith("/images/") || normalizedPath.startsWith("images/")) {
    return normalizedPath.startsWith("/") ? normalizedPath : `/${normalizedPath}`
  }

  // 3. Path từ API đã có prefix /api/
  if (normalizedPath.startsWith("/api/")) {
    return withSiteOrigin(normalizedPath)
  }

  // 4. Path thô (vd: images/2026/03/...) -> mặc định thêm /api/admin/uploads/serve/
  let cleanPath = normalizedPath
  
  // Loại bỏ các prefix cũ nếu có để tránh lặp lại
  const prefixesToStrip = ["/uploads/", "uploads/", "/api/admin/uploads/serve/", "api/admin/uploads/serve/"]
  for (const prefix of prefixesToStrip) {
    if (cleanPath.startsWith(prefix)) {
      cleanPath = cleanPath.substring(prefix.length)
      break
    }
  }
  
  if (cleanPath.startsWith("/")) {
    cleanPath = cleanPath.substring(1)
  }
  
  try {
    let basePath = "/api"
    
    if (apiUrl.startsWith("http")) {
      const url = new URL(apiUrl)
      basePath = url.pathname.replace(/\/$/, "")
      return withSiteOrigin(`${basePath}/uploads/${cleanPath}`)
    } else {
      basePath = apiUrl.replace(/\/$/, "")
    }
    
    // Sử dụng route phục vụ ảnh chuẩn của API: /api/uploads/
    // Trả về relative path để Next.js Image Optimization fetch thông qua rewrites (next.config.ts)
    return `${basePath}/uploads/${cleanPath}`
  } catch {
    return `/api/uploads/${cleanPath}`
  }
}

/**
 * Extracts error message from a Response object
 */
export const extractErrorMessage = async (response: Response): Promise<string> => {
  const data = await parseJsonSafe<{ error?: string; message?: string }>(response)
  if (data) {
    if (typeof data.error === "string") return data.error
    if (typeof data.message === "string") return data.message
    return JSON.stringify(data)
  }
  try {
    return await response.clone().text()
  } catch {
    return "Không xác định"
  }
}

/**
 * HTTP status code error messages
 */
const STATUS_MESSAGES: Record<number, string> = {
  401: "Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.",
  403: "Bạn không có quyền thực hiện hành động này.",
  404: "Không tìm thấy tài nguyên.",
  500: "Lỗi máy chủ. Vui lòng thử lại sau.",
}

/**
 * Extracts error message from axios error object
 * Returns a user-friendly error message, combining all validation errors if available
 */
export const extractAxiosErrorMessage = (
  error: unknown,
  defaultMessage = "Đã xảy ra lỗi"
): string => {
  if (error && typeof error === "object" && "response" in error) {
    const axiosError = error as { 
      response: { 
        status: number; 
        data: { 
          message?: string | string[]; 
          error?: string;
          errors?: Array<{ message: string }>;
        } 
      } 
    }
    
    const status = axiosError.response?.status
    const data = axiosError.response?.data
    
    // Check for validation errors (usually array of messages)
    if (data?.errors && Array.isArray(data.errors)) {
      return data.errors.map(err => err.message).join(". ")
    }
    
    if (data?.message) {
      if (Array.isArray(data.message)) {
        return data.message.join(". ")
      }
      return data.message
    }
    
    if (data?.error) return data.error
    
    // Fallback to status messages
    if (status && STATUS_MESSAGES[status]) {
      return STATUS_MESSAGES[status]
    }
  }
  
  if (error instanceof Error) {
    return error.message
  }
  
  return defaultMessage
}
